import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, Paperclip } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { SearchableSelect, type SearchableOption } from '@/components/ui/searchable-select'
import {
  actionItemsApi,
  type ActionItem,
  type ActionItemPriority,
  type ActionItemStatus,
} from '@/api/actionItems'
import {
  useActionItemAssignees,
  useCreateActionItem,
  useUpdateActionItem,
} from '@/hooks/useActionItems'
import { PRIORITY_LABELS, STATUS_LABELS, localToday, sourceLink } from '@/lib/actionItems'
import { dueDateProblem } from '@/lib/dueDate'
import { cn } from '@/lib/utils'

/**
 * The form an item is raised on: from the form's "+" menu, or from the Action
 * Items page while it lists that form's items.
 */
export interface FormSource {
  submissionId: string
  /** Absent when only the submission is known; the server names it either way. */
  formName?: string | null
  /** The run the form is open in, to link back to. */
  path?: string | null
}

const PRIORITIES: ActionItemPriority[] = ['LOW', 'MED', 'HIGH']

/** A person and a group share one picker: the value says which. */
function assigneeValue(item: Pick<ActionItem, 'assignedUser' | 'assignedGroup'> | null): string {
  if (item?.assignedUser) return `user:${item.assignedUser.id}`
  if (item?.assignedGroup) return `group:${item.assignedGroup.id}`
  return ''
}

function splitAssignee(value: string): { assignedUserId: string | null; assignedGroupId: string | null } {
  if (value.startsWith('user:')) return { assignedUserId: value.slice(5), assignedGroupId: null }
  if (value.startsWith('group:')) return { assignedUserId: null, assignedGroupId: value.slice(6) }
  return { assignedUserId: null, assignedGroupId: null }
}

/** A row of mutually exclusive buttons, for a short fixed set of choices. */
function Segmented<T extends string>({
  value,
  options,
  onChange,
  disabled,
  label,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (value: T) => void
  disabled?: boolean
  label: string
}) {
  return (
    <div role='radiogroup' aria-label={label} className='flex flex-wrap gap-1.5'>
      {options.map(o => (
        <button
          key={o.value}
          type='button'
          role='radio'
          aria-checked={value === o.value}
          disabled={disabled}
          onClick={() => onChange(o.value)}
          className={cn(
            'rounded-md border px-3 py-1.5 text-sm transition-colors disabled:opacity-60',
            value === o.value
              ? 'border-primary bg-primary text-primary-foreground'
              : 'bg-background hover:bg-accent',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Raise or edit an Action Item (z8nrz7fkfh).
 *
 * Its Source is where it is raised from, never picked: a form (`formSource`),
 * otherwise General. Editing, the status
 * can be set to Started or Completed; Open and Assigned follow the assignment.
 */
export function ActionItemDialog({
  open,
  onOpenChange,
  item,
  formSource,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Editing this item; absent, a new one is raised. */
  item?: ActionItem | null
  /** Raising on a form: its Source is that form. Absent, it is General. */
  formSource?: FormSource
  onSaved?: (item: ActionItem) => void
}) {
  const editing = !!item
  const readOnly = editing && !item.canEdit
  const today = localToday()

  const [description, setDescription] = useState('')
  const [assignee, setAssignee] = useState('')
  const [priority, setPriority] = useState<ActionItemPriority>('MED')
  const [dueDate, setDueDate] = useState('')
  const [comments, setComments] = useState('')
  const [status, setStatus] = useState<ActionItemStatus>('OPEN')
  const [error, setError] = useState<string | null>(null)

  // Fresh each time it opens, from the item when editing.
  useEffect(() => {
    if (!open) return
    setDescription(item?.description ?? '')
    setAssignee(assigneeValue(item ?? null))
    setPriority(item?.priority ?? 'MED')
    setDueDate(item?.dueDate ?? '')
    setComments(item?.comments ?? '')
    setStatus(item?.status ?? 'OPEN')
    setError(null)
  }, [open, item])

  const assignees = useActionItemAssignees(open)
  const create = useCreateActionItem()
  const update = useUpdateActionItem()
  const saving = create.isPending || update.isPending

  const assigneeOptions = useMemo<SearchableOption[]>(() => {
    const data = assignees.data
    if (!data) return []
    return [
      { value: '', label: 'Unassigned' },
      ...data.users.map(u => ({ value: `user:${u.id}`, label: `${u.name} (${u.email})` })),
      ...data.groups.map(g => ({ value: `group:${g.id}`, label: `Group: ${g.name}` })),
    ]
  }, [assignees.data])

  // Keeps a current assignee readable while the roster loads, or after they left.
  const selectedAssignee = useMemo<SearchableOption | undefined>(() => {
    if (item?.assignedUser) return { value: `user:${item.assignedUser.id}`, label: item.assignedUser.name }
    if (item?.assignedGroup) return { value: `group:${item.assignedGroup.id}`, label: `Group: ${item.assignedGroup.name}` }
    return undefined
  }, [item])

  const assigned = assignee !== ''
  // Open and Assigned are not chosen: whichever the assignment says is offered.
  const statusOptions: { value: ActionItemStatus; label: string }[] = [
    assigned ? { value: 'ASSIGNED', label: STATUS_LABELS.ASSIGNED } : { value: 'OPEN', label: STATUS_LABELS.OPEN },
    { value: 'STARTED', label: STATUS_LABELS.STARTED },
    { value: 'COMPLETED', label: STATUS_LABELS.COMPLETED },
  ]
  const shownStatus: ActionItemStatus =
    status === 'STARTED' || status === 'COMPLETED' ? status : assigned ? 'ASSIGNED' : 'OPEN'

  const save = async () => {
    setError(null)
    if (!description.trim()) return setError('Enter a description.')
    const dueProblem = dueDateProblem({ today, dueDate, current: item?.dueDate ?? '', earlyStart: null })
    if (dueProblem) return setError(dueProblem)
    const { assignedUserId, assignedGroupId } = splitAssignee(assignee)
    try {
      const saved = editing
        ? await update.mutateAsync({
            id: item.id,
            input: {
              description: description.trim(),
              assignedUserId,
              assignedGroupId,
              priority,
              dueDate,
              comments: comments.trim() || null,
              status: shownStatus,
            },
          })
        : await create.mutateAsync({
            description: description.trim(),
            ...(assignedUserId ? { assignedUserId } : {}),
            ...(assignedGroupId ? { assignedGroupId } : {}),
            priority,
            dueDate,
            ...(comments.trim() ? { comments: comments.trim() } : {}),
            ...(formSource
              ? {
                  sourceType: 'FORM' as const,
                  sourceId: formSource.submissionId,
                  ...(formSource.path ? { sourcePath: formSource.path } : {}),
                }
              : { sourceType: 'GENERAL' as const }),
          })
      onSaved?.(saved)
      onOpenChange(false)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the action item.')
    }
  }

  const openFile = async (attachmentId: string) => {
    try {
      const { url } = await actionItemsApi.downloadUrl(attachmentId)
      window.open(url, '_blank', 'noopener,noreferrer')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not open the file.')
    }
  }

  const link = item ? sourceLink(item) : null
  const sourceText = item
    ? [item.sourceArea, item.sourceLabel].filter(Boolean).join(': ')
    : formSource
      ? `Form${formSource.formName ? `: ${formSource.formName}` : ''}`
      : 'General'

  return (
    <Dialog open={open} onOpenChange={o => !saving && onOpenChange(o)}>
      <DialogContent className='max-w-lg'>
        <DialogHeader>
          <DialogTitle>{editing ? `Action Item A#${item.number}` : 'New Action Item'}</DialogTitle>
          <DialogDescription>
            {editing
              ? `Raised by ${item.createdByName} on ${localToday(new Date(item.createdAt))}.`
              : formSource
                ? `On the form ${formSource.formName ?? 'this list is filtered to'}.`
                : 'A follow-up for a person or a group, with a due date.'}
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='text-sm'>
            <span className='text-muted-foreground'>Source: </span>
            {link ? (
              <Link to={link} className='font-medium text-primary underline' onClick={() => onOpenChange(false)}>
                {sourceText}
              </Link>
            ) : (
              <span className='font-medium'>{sourceText}</span>
            )}
          </div>

          <div className='space-y-1.5'>
            <Label htmlFor='ai-description'>Description</Label>
            <Textarea
              id='ai-description'
              value={description}
              onChange={e => setDescription(e.target.value)}
              disabled={readOnly}
              maxLength={2000}
              rows={3}
            />
          </div>

          <div className='space-y-1.5'>
            <Label>Assigned To</Label>
            <SearchableSelect
              value={assignee}
              onChange={setAssignee}
              options={assigneeOptions}
              selectedOption={selectedAssignee}
              placeholder='Unassigned'
              disabled={readOnly}
              emptyMessage={assignees.isLoading ? 'Loading…' : 'Nobody to assign.'}
            />
          </div>

          <div className='grid gap-4 sm:grid-cols-2'>
            <div className='space-y-1.5'>
              <Label>Priority</Label>
              <Segmented
                label='Priority'
                value={priority}
                options={PRIORITIES.map(p => ({ value: p, label: PRIORITY_LABELS[p] }))}
                onChange={setPriority}
                disabled={readOnly}
              />
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='ai-due'>Due Date</Label>
              <Input
                id='ai-due'
                type='date'
                value={dueDate}
                min={today}
                onChange={e => setDueDate(e.target.value)}
                disabled={readOnly}
              />
            </div>
          </div>

          {editing && (
            <div className='space-y-1.5'>
              <Label>Status</Label>
              <Segmented label='Status' value={shownStatus} options={statusOptions} onChange={setStatus} disabled={readOnly} />
            </div>
          )}

          <div className='space-y-1.5'>
            <Label htmlFor='ai-comments'>Comments</Label>
            <Textarea
              id='ai-comments'
              value={comments}
              onChange={e => setComments(e.target.value)}
              disabled={readOnly}
              maxLength={2000}
              rows={2}
            />
          </div>

          {editing && item.attachments.length > 0 && (
            <div className='space-y-1.5'>
              <Label>Attachments</Label>
              <ul className='space-y-1'>
                {item.attachments.map(a => (
                  <li key={a.id}>
                    <button
                      type='button'
                      onClick={() => void openFile(a.id)}
                      className='flex items-center gap-1.5 text-sm text-primary hover:underline'
                    >
                      <Paperclip className='h-3.5 w-3.5' />
                      {a.fileName}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {readOnly && (
            <p className='text-sm text-muted-foreground'>
              Only whoever raised it, its assignee, or someone who manages action items can change it.
            </p>
          )}
          {error && (
            <p role='alert' className='text-sm text-destructive'>
              {error}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)} disabled={saving}>
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly && (
            <Button onClick={() => void save()} disabled={saving}>
              {saving && <Loader2 className='mr-1.5 h-4 w-4 animate-spin' />}
              {editing ? 'Save' : 'Add'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
